import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, MoreHorizontal, Pencil, Power, Loader2, UserCog } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", password: "", mobile: "", employee_id: "", designation: "", department: "", role_id: "", facility_ids: [] };

export function UsersManager({ orgId, canEdit = true, testId = "users-manager" }) {
  const [rows, setRows] = useState(null);
  const [roles, setRoles] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const qs = (extra = "") => `organisation_id=${orgId}${extra}`;
  const load = useCallback(() => {
    if (!orgId) return;
    setRows(null);
    api.get(`/users?${qs()}`).then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get(`/roles?${qs()}`).then((r) => setRoles(r.data)).catch(() => {});
    api.get(`/facilities?${qs()}`).then((r) => setFacilities(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);
  useEffect(load, [load]);

  const roleScope = (roleId) => roles.find((r) => (r.id || r.code) === roleId)?.scope;
  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...EMPTY, ...u, password: "", role_id: u.role_id || "", facility_ids: u.facility_ids || [] }); setOpen(true); };

  const toggleFacility = (fid) => setForm((f) => ({ ...f, facility_ids: f.facility_ids.includes(fid) ? f.facility_ids.filter((x) => x !== fid) : [...f.facility_ids, fid] }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, organisation_id: orgId };
      if (editing) await api.put(`/users/${editing.id}`, payload);
      else await api.post("/users", payload);
      toast.success(editing ? "User updated" : "User created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const toggle = async (u) => { try { await api.patch(`/users/${u.id}/status`); toast.success("Status updated"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const needsFacilities = form.role_id && roleScope(form.role_id) === "facility";

  return (
    <div data-testid={testId}>
      {canEdit && <div className="flex justify-end mb-4"><Button onClick={openCreate} disabled={!orgId} className="bg-blue-700 hover:bg-blue-800" data-testid="create-user-button"><Plus className="h-4 w-4 mr-1.5" /> New User</Button></div>}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {rows === null ? <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>User</TableHead><TableHead>Employee ID</TableHead><TableHead>Role</TableHead>
              <TableHead>Facilities</TableHead><TableHead>Status</TableHead>{canEdit && <TableHead className="text-right">Actions</TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((u) => (
                <TableRow key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <span className="h-8 w-8 rounded-md bg-slate-900 text-white grid place-items-center text-xs font-semibold">{(u.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("")}</span>
                      <div><div className="font-medium text-slate-800">{u.name}</div><div className="text-xs text-slate-400">{u.email}</div></div>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{u.employee_id || "—"}</TableCell>
                  <TableCell className="text-sm"><span className="inline-flex items-center gap-1.5 text-slate-700"><UserCog className="h-3.5 w-3.5 text-slate-400" />{u.role_name || "—"}</span></TableCell>
                  <TableCell className="text-xs text-slate-500">{u.org_wide ? "All facilities" : `${u.facility_ids?.length || 0} facility(s)`}</TableCell>
                  <TableCell><StatusBadge status={u.status} /></TableCell>
                  {canEdit && <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`user-actions-${u.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggle(u)} data-testid={`user-toggle-${u.id}`}><Power className="h-4 w-4 mr-2" /> {u.status === "active" ? "Deactivate" : "Activate"}</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>}
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">No users yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto thin-scroll" data-testid="user-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit User" : "New User"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
              {!editing && <div className="space-y-1.5"><Label>Password</Label><Input type="text" value={form.password} placeholder="Welcome@2026" onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>}
              <div className="space-y-1.5"><Label>Mobile</Label><Input value={form.mobile || ""} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="user-mobile-input" /></div>
              <div className="space-y-1.5"><Label>Employee ID</Label><Input value={form.employee_id || ""} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} data-testid="user-empid-input" /></div>
              <div className="space-y-1.5"><Label>Designation</Label><Input value={form.designation || ""} onChange={(e) => setForm({ ...form, designation: e.target.value })} data-testid="user-designation-input" /></div>
              <div className="space-y-1.5"><Label>Department</Label><Input value={form.department || ""} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })} data-testid="user-role-select">
                <option value="">Select a role…</option>
                {roles.map((r) => <option key={r.id || r.code} value={r.id || r.code}>{r.name} ({r.scope})</option>)}
              </select>
            </div>
            {needsFacilities && (
              <div className="space-y-1.5">
                <Label>Assigned facilities</Label>
                <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-44 overflow-y-auto thin-scroll">
                  {facilities.map((f) => (
                    <label key={f.id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-slate-50" data-testid={`user-facility-${f.id}`}>
                      <Checkbox checked={form.facility_ids.includes(f.id)} onCheckedChange={() => toggleFacility(f.id)} />
                      <span className="text-sm text-slate-700">{f.name}</span><span className="text-xs text-slate-400 font-mono ml-auto">{f.code}</span>
                    </label>
                  ))}
                  {facilities.length === 0 && <div className="px-3 py-3 text-sm text-slate-400">No facilities available.</div>}
                </div>
                <p className="text-xs text-slate-400">Facility-scoped roles apply only to selected facilities. Organisation-scoped roles apply to all facilities.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name || !form.email || !form.role_id} className="bg-blue-700 hover:bg-blue-800" data-testid="user-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save user"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
